import { useState } from 'react';
import { Button, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import { ConfirmButton, ErrorBox, TableScroller, useNotice } from '../../components/common';
import {
  stats,
  AUTH_MODES,
  type AuthMode,
  type IssuedToken,
  type LogFilter,
  type OrganizationDetail,
  type TrackedMethod,
} from '../../api/stats';
import { formatDateTime } from '../../lib/format';
import { SEVERITIES, statsError } from './shared';

/** Client mirror of the collector's checks (regexes are checked there with .NET's engine). */
export function monitoringProblems(filters: LogFilter[], methods: TrackedMethod[]): string[] {
  const p: string[] = [];
  if (filters.some((f) => !f.regex.trim())) p.push('every filter needs a regex');
  if (new Set(filters.map((f) => f.line)).size !== filters.length) p.push('filter lines must be unique');
  if (methods.some((m) => !m.method.trim())) p.push('every tracked method needs a name');
  if (new Set(methods.map((m) => m.method.toLowerCase())).size !== methods.length)
    p.push('method names must be unique');
  for (const f of filters) {
    try {
      new RegExp(f.regex);
    } catch {
      p.push(`filter ${f.line}: not a valid regular expression`);
    }
  }
  return p;
}

/** The new token, shown exactly once. */
function TokenDialog({ issued, onClose }: { issued: IssuedToken; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog
      open
      onClose={onClose}
      title="New collector token"
      maxWidth={640}
      footer={<Button onClick={onClose}>I have stored it</Button>}
    >
      <p>This is the only time the token is shown; the collector keeps only its hash.</p>
      <div className="sx-token" data-testid="issued-token">
        {issued.token}
      </div>
      <div className="sx-links" style={{ marginTop: 8 }}>
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            void navigator.clipboard?.writeText(issued.token).then(
              () => setCopied(true),
              () => setCopied(false)
            )
          }
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <div className="sx-warning" role="alert">
        Configure it as the RemoteDataCollector token on <b>every WorkServer</b> of this organization. The
        previous token stops working, but each collector instance may still accept it for up to 15 minutes —
        WorkServers that keep the old one go silent after that.
      </div>
    </Dialog>
  );
}

const SEVERITY_OPTIONS = [
  { value: '', label: '— keep —' },
  ...Object.entries(SEVERITIES).map(([v, l]) => ({ value: v, label: l })),
];

const AUTH_MODE_HELP: Record<AuthMode, string> = {
  legacy: 'Only the collector token. Bearer tokens are rejected.',
  both: 'The collector token or an AUTH bearer with datacollector:write. Use while WorkServers move over.',
  auth: 'Only an AUTH bearer. The collector token stops working for this organization.',
};

/**
 * S1: which credential ingest accepts. Applies only while the collector runs `NyceLogicAuth:Mode=Optional`; a bearer's
 * tenant must reach this organization through an AUTH `pms:<customerId>` alias.
 */
function AuthModeCard({
  org,
  canAdmin,
  onSaved,
  notify,
}: {
  org: OrganizationDetail;
  canAdmin: boolean;
  onSaved: (o: OrganizationDetail) => void;
  notify: { success: (t: string) => void; error: (t: string) => void };
}) {
  const current = org.auth!.mode;
  const [mode, setMode] = useState<AuthMode>(current);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      onSaved(await stats.setAuthMode(org.customerId, mode));
      notify.success(`Ingest credential set to ${mode}. Collector instances pick it up within a minute.`);
    } catch (e) {
      notify.error(statsError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="nyk-card" data-testid="auth-mode">
      <div className="nyk-card-head">
        <h2>Ingest credential</h2>
        {org.auth!.changedAt && (
          <span className="nyk-ui-hint">
            changed {formatDateTime(org.auth!.changedAt)}
            {org.auth!.changedBy && ` by ${org.auth!.changedBy}`}
          </span>
        )}
      </div>
      <div className="nyk-inline-form">
        <Select
          label="Accept"
          value={mode}
          onChange={(v) => setMode(v as AuthMode)}
          options={AUTH_MODES.map((m) => ({ value: m, label: m }))}
          disabled={!canAdmin}
          hint={AUTH_MODE_HELP[mode]}
        />
        {canAdmin && (
          <ConfirmButton
            variant="secondary"
            disabled={mode === current}
            loading={busy}
            message={
              mode === 'auth'
                ? `Accept only AUTH bearers for ${org.name ?? org.customerId}? WorkServers still sending the collector token are rejected.`
                : `Set the ingest credential of ${org.name ?? org.customerId} to ${mode}?`
            }
            onConfirm={() => void save()}
          >
            Save
          </ConfirmButton>
        )}
      </div>
      <p className="nyk-card-intro" style={{ marginTop: 12, marginBottom: 0 }}>
        Only applies while the collector runs <code>NyceLogicAuth:Mode=Optional</code>. A bearer's tenant must
        carry the AUTH alias <code>pms:{org.customerId}</code> (or its customer must). Check the migration
        view on the Identity dashboard before switching to <code>auth</code>.
      </p>
    </div>
  );
}

/**
 * Monitoring settings of one organization (replaces PMS's monitoring tab): switch, location override, log filters,
 * tracked methods, and the collector token (hashed; issued and shown once).
 */
export function MonitoringTab({
  org,
  canAdmin,
  derived,
  onSaved,
  onTokenIssued,
}: {
  org: OrganizationDetail;
  canAdmin: boolean;
  derived: { value: string; from: string } | null;
  onSaved: (o: OrganizationDetail) => void;
  /** Re-read the organization (token issued at/by). */
  onTokenIssued: () => void;
}) {
  const [active, setActive] = useState(org.active);
  const [location, setLocation] = useState(org.location ?? '');
  const [skip, setSkip] = useState(org.alarmSkipWorkserver ?? '');
  const [filters, setFilters] = useState<LogFilter[]>(org.filters);
  const [methods, setMethods] = useState<TrackedMethod[]>(org.performance);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedToken | null>(null);
  const toast = useNotice();
  const problems = monitoringProblems(filters, methods);
  const ro = !canAdmin;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await stats.updateMonitoring(org.customerId, {
        active,
        location: location.trim(),
        alarmSkipWorkserver: skip.trim(),
        filters,
        performance: methods,
      });
      onSaved(saved);
      toast.success('Saved. Ingest picks up monitoring changes within 15 minutes.');
    } catch (e) {
      setError(statsError(e));
    } finally {
      setBusy(false);
    }
  };
  const issue = async () => {
    try {
      setIssued(await stats.issueToken(org.customerId));
    } catch (e) {
      toast.error(statsError(e));
    }
  };
  const setFilter = (i: number, patch: Partial<LogFilter>) =>
    setFilters((cur) => cur.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const setMethod = (i: number, patch: Partial<TrackedMethod>) =>
    setMethods((cur) => cur.map((m, j) => (j === i ? { ...m, ...patch } : m)));

  return (
    <section className="sx-body" data-testid="monitoring">
      {toast.element}
      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Collector token</h2>
        </div>
        <p className="nyk-card-intro">
          {org.token.issued ? (
            <>
              Issued {formatDateTime(org.token.issuedAt)}
              {org.token.issuedBy && ` by ${org.token.issuedBy}`}. Only its hash is stored, so it cannot be
              shown again.
            </>
          ) : (
            'No hashed token yet: WorkServers may still send the token PMS showed, until one is issued here.'
          )}
        </p>
        {canAdmin && (
          <ConfirmButton
            variant="secondary"
            message={`Issue a new collector token for ${org.name ?? org.customerId}? Every WorkServer must be reconfigured; the old token stops working within 15 minutes.`}
            onConfirm={() => void issue()}
          >
            {org.token.issued ? 'Regenerate token' : 'Issue token'}
          </ConfirmButton>
        )}
      </div>

      {org.auth && <AuthModeCard org={org} canAdmin={canAdmin} onSaved={onSaved} notify={toast} />}

      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Settings</h2>
          <span className="nyk-ui-hint">ingest reads them within 15 minutes</span>
        </div>
        <ErrorBox error={error} />
        <div className="nyk-form">
          <Switch checked={active} onChange={setActive} disabled={ro} label="Monitoring on" />
          <div />
          <div className="nyk-ui-field">
            <span>Location (derived)</span>
            <span data-testid="derived-location">
              {derived ? (
                <>
                  <b>{derived.value}</b> <span className="nyk-ui-hint">from {derived.from}</span>
                </>
              ) : (
                <span className="nyk-ui-hint">no linked environment</span>
              )}
            </span>
          </div>
          <TextField
            label="Location override"
            value={location}
            disabled={ro}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={derived?.value ?? 'e.g. On-prem, GCP, Proact'}
            hint="empty = the derived value"
          />
          <div className="nyk-span2">
            <TextField
              label="Skip WorkServers (regex)"
              value={skip}
              disabled={ro}
              onChange={(e) => setSkip(e.target.value)}
              hint="error logs from matching WorkServer names are not stored"
            />
          </div>
        </div>
      </div>

      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Log filters</h2>
          <span className="nyk-ui-hint">first matching regex wins; exclude drops the log record</span>
        </div>
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="filters">
            <thead>
              <tr>
                <th>Line</th>
                <th>Regex</th>
                <th>Include</th>
                <th>Severity</th>
                {!ro && <th />}
              </tr>
            </thead>
            <tbody>
              {filters.map((f, i) => (
                <tr key={i}>
                  <td data-label="Line">{f.line}</td>
                  <td className="nyk-card-title">
                    <TextField
                      aria-label={`Filter ${f.line} regex`}
                      value={f.regex}
                      disabled={ro}
                      onChange={(e) => setFilter(i, { regex: e.target.value })}
                    />
                  </td>
                  <td data-label="Include">
                    <Switch
                      checked={f.include}
                      disabled={ro}
                      onChange={(v) => setFilter(i, { include: v })}
                      label={f.include ? 'include' : 'exclude'}
                    />
                  </td>
                  <td data-label="Severity">
                    <Select
                      aria-label={`Filter ${f.line} severity`}
                      value={f.severity == null ? '' : String(f.severity)}
                      disabled={ro}
                      onChange={(v) => setFilter(i, { severity: v === '' ? null : Number(v) })}
                      options={SEVERITY_OPTIONS}
                    />
                  </td>
                  {!ro && (
                    <td className="nyk-row-actions">
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setFilters((cur) => cur.filter((_, j) => j !== i))}
                      >
                        Remove
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {!filters.length && <p className="nyk-ui-hint">No filters: every error log is stored.</p>}
        {!ro && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setFilters((cur) => [
                ...cur,
                {
                  line: Math.max(0, ...cur.map((f) => f.line)) + 1,
                  regex: '',
                  include: false,
                  severity: null,
                },
              ])
            }
          >
            Add filter
          </Button>
        )}
      </div>

      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Tracked methods</h2>
          <span className="nyk-ui-hint">
            {methods.length} methods · incident check runs nightly on the ticked ones
          </span>
        </div>
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="methods">
            <thead>
              <tr>
                <th>Method</th>
                <th>Active</th>
                <th>Incident check</th>
                <th>Check WorkServers</th>
                {!ro && <th />}
              </tr>
            </thead>
            <tbody>
              {methods.map((m, i) => (
                <tr key={i}>
                  <td className="nyk-card-title">
                    <TextField
                      aria-label="Method"
                      value={m.method}
                      disabled={ro}
                      onChange={(e) => setMethod(i, { method: e.target.value })}
                    />
                  </td>
                  <td data-label="Active">
                    <Switch checked={m.active} disabled={ro} onChange={(v) => setMethod(i, { active: v })} />
                  </td>
                  <td data-label="Incident check">
                    <Switch
                      checked={m.incidentCheck}
                      disabled={ro}
                      onChange={(v) => setMethod(i, { incidentCheck: v })}
                    />
                  </td>
                  <td data-label="Check WorkServers">
                    <TextField
                      aria-label={`${m.method} incident check WorkServers`}
                      value={(m.incidentCheckWorkservers ?? []).join(', ')}
                      disabled={ro || !m.incidentCheck}
                      placeholder="ALL"
                      onChange={(e) => {
                        const list = e.target.value
                          .split(',')
                          .map((x) => x.trim())
                          .filter(Boolean);
                        setMethod(i, { incidentCheckWorkservers: list.length ? list : null });
                      }}
                    />
                  </td>
                  {!ro && (
                    <td className="nyk-row-actions">
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setMethods((cur) => cur.filter((_, j) => j !== i))}
                      >
                        Remove
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {!ro && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setMethods((cur) => [...cur, { method: '', active: true, incidentCheck: false }])}
          >
            Add method
          </Button>
        )}
      </div>

      {canAdmin && (
        <div className="sx-toolbar">
          <Button loading={busy} disabled={problems.length > 0} onClick={() => void save()}>
            Save monitoring settings
          </Button>
          {problems.length > 0 && <span className="nyk-ui-hint">To save: {problems.join(' · ')}</span>}
        </div>
      )}
      {issued && (
        <TokenDialog
          issued={issued}
          onClose={() => {
            setIssued(null);
            onTokenIssued();
          }}
        />
      )}
    </section>
  );
}
